import "@fontsource/space-grotesk/300.css";
import "@fontsource/space-grotesk/400.css";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/600.css";
import "@fontsource/space-grotesk/700.css";
import React, { useState, useEffect, Suspense } from "react";
import { BrowserRouter, Routes, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "@/App.css";
import { Toaster } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import SmoothScroll from "@/components/SmoothScroll";
import Preloader from "@/components/Preloader";
import { AuthHandler, ProtectedRoute, Navbar } from "@/pages/MainPages";
import MobileBottomNav from "@/components/MobileBottomNav";
import MobileHeader from "@/components/MobileHeader";
import SkipToContent from "@/components/SkipToContent";
import useRealTimeUpdates from "@/hooks/useRealTimeUpdates";
import ScreenShield from "@/components/ScreenShield";
import CookieConsent from "@/components/CookieConsent";
import FloatingOfficerDock from "@/components/FloatingOfficerDock";
import PastDueBanner from "@/components/PastDueBanner";
import { initGA, trackPageView } from "@/lib/analytics";
import { secureStorage } from "@/lib/secureStorage";
import AppRoutes from "@/AppRoutes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Habilita el envío de cookies httpOnly (`session_token`) en cada request.
// El backend prefiere la cookie sobre el header Authorization, lo que reduce
// la superficie de XSS mientras mantenemos retro-compatibilidad con localStorage.
axios.defaults.withCredentials = true;

// Global axios interceptor - always include Bearer token if available
axios.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem("proxima_session_token");
    if (raw) {
      const token = decodeURIComponent(escape(atob(raw)));
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
  } catch (e) { /* token decode failed, skip */ }
  return config;
});

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-black">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mb-4"></div>
      <p className="text-gray-400">Cargando...</p>
    </div>
  </div>
);

function AppContent() {
  const [user, setUser] = useState(() => secureStorage.getUser());
  const [loading, setLoading] = useState(true);
  const [brandSetupComplete, setBrandSetupComplete] = useState(null);
  const [isProcessingAuth, setIsProcessingAuth] = useState(false);
  const [preloaderDone, setPreloaderDone] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const realtimeUpdates = useRealTimeUpdates(user);

  useEffect(() => {
    initGA();
    if (location.hash.includes("session_id")) {
      setIsProcessingAuth(true);
      setLoading(false);
    } else {
      checkExistingSession();
    }
  }, [location.hash]);

  // Track page views on route change
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);

  const checkExistingSession = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      secureStorage.setUser(response.data);
      try {
        const brandStatus = await axios.get(`${API}/brand-setup/status`);
        setBrandSetupComplete(brandStatus.data.is_complete);
      } catch (e) {
        console.error("Brand setup status check failed:", e);
        setBrandSetupComplete(false);
      }
    } catch (e) {
      console.error("Auth check failed:", e);
      // If we have a cached user and token, keep them (token auth works even if cookie fails)
      const cachedUser = secureStorage.getUser();
      const hasToken = !!secureStorage.getToken();
      if (cachedUser && hasToken) {
        setUser(cachedUser);
      } else {
        secureStorage.clearAll();
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAuthComplete = (userData) => {
    setUser(userData);
    setLoading(false);
    setIsProcessingAuth(false);
    axios.get(`${API}/brand-setup/status`)
      .then(res => setBrandSetupComplete(res.data.is_complete))
      .catch(() => setBrandSetupComplete(false));
    navigate("/dashboard", { replace: true });
  };

  const handleBrandSetupComplete = () => {
    setBrandSetupComplete(true);
    navigate("/dashboard", { replace: true });
  };

  const handleLogout = () => {
    setUser(null);
    setBrandSetupComplete(null);
    secureStorage.clearAll();
  };

  if (isProcessingAuth) return <AuthHandler onAuthComplete={handleAuthComplete} />;

  const showNav = user && location.pathname !== "/brand-setup";

  return (
    <ErrorBoundary>
      {!preloaderDone && !isProcessingAuth && location.pathname === "/" && (
        <Preloader onComplete={() => setPreloaderDone(true)} />
      )}
      <div className="app-container">
        <ScreenShield />
        <SkipToContent />
        <Toaster
          position="bottom-right"
          richColors
          duration={3000}
          toastOptions={{
            style: { background: "rgba(17, 24, 39, 0.95)", border: "1px solid rgba(139, 92, 246, 0.3)", color: "white" },
            className: "backdrop-blur-sm",
          }}
        />
        {showNav && <Navbar user={user} onLogout={handleLogout} realtimeUpdates={realtimeUpdates} />}
        {showNav && <MobileHeader user={user} onLogout={handleLogout} realtimeUpdates={realtimeUpdates} />}
        {showNav && <MobileBottomNav />}
        <main id="main-content" role="main">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {AppRoutes({
                user,
                loading,
                brandSetupComplete,
                onAuthComplete: handleAuthComplete,
                onBrandSetupComplete: handleBrandSetupComplete,
              })}
            </Routes>
          </Suspense>
        </main>
        <CookieConsent />
        <FloatingOfficerDock />
        <PastDueBanner />
      </div>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SmoothScroll>
        <AppContent />
      </SmoothScroll>
    </BrowserRouter>
  );
}

export default App;
